import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card, Spinner } from 'react-bootstrap';
import { apiService } from '../../../services/api';
import './Registration.scss';

interface WhitelistRegistrationProps {
  event: any;
  category: any;
  onSuccess?: (data: any) => void;
  onCancel?: () => void;
}

interface WhitelistData {
  whitelist_request: {
    id: number;
    name: string;
    email: string;
    phone: string;
    status: string;
  };
  whitelist_data: { [key: string]: any };
}

const WhitelistRegistration: React.FC<WhitelistRegistrationProps> = ({ 
  event, 
  category, 
  onSuccess, 
  onCancel 
}) => {
  const [whitelistData, setWhitelistData] = useState<WhitelistData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Fetch whitelist data on component mount
  useEffect(() => {
    const fetchWhitelistData = async () => {
      setLoading(true);
      setError(null);
      
      console.log('Fetching whitelist data for:', { event_slug: event.slug, category_id: category.id });
      
      try {
        // First, get CSRF token from the whitelist endpoint
        const csrfResponse = await fetch('http://localhost:8000/api/register/whitelist/', {
          method: 'GET',
          credentials: 'include'
        });
        const csrfData = await csrfResponse.json();
        if (csrfData.csrf_token) {
          localStorage.setItem('csrf_token', csrfData.csrf_token);
          console.log('CSRF token stored:', csrfData.csrf_token);
        }
        
        // This would be a new API endpoint to get whitelist data for approved users
        const response = await apiService.registration.getWhitelistData({
          event_slug: event.slug,
          category_id: category.id
        });

        console.log('Whitelist data response:', response);

        if (response.success) {
          setWhitelistData(response.data);
        } else {
          setError(response.error || 'Failed to load whitelist data');
        }
      } catch (error) {
        console.error('Error fetching whitelist data:', error);
        setError('Failed to load whitelist data');
      }
      
      setLoading(false);
    };

    fetchWhitelistData();
  }, [event.slug, category.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    if (!whitelistData) {
      setError('Whitelist data not available');
      setSubmitting(false);
      return;
    }

    const requestData = {
      event_slug: event.slug,
      category_id: category.id,
      quantity: 1, // Default to 1 ticket
      name: whitelistData.whitelist_request.name,
      email: whitelistData.whitelist_request.email,
      phone: whitelistData.whitelist_request.phone,
      custom_fields: whitelistData.whitelist_data || {}
    };
    
    console.log('Whitelist registration request data:', requestData);
    console.log('Whitelist data available:', whitelistData);
    
    const response = await apiService.registration.whitelist(requestData);

    if (response.success) {
      setSuccess(true);
      
      // Dispatch cart update event to notify header
      window.dispatchEvent(new CustomEvent('cartUpdated', {
        detail: { message: 'Cart updated successfully' }
      }));
      
      onSuccess?.(response);
    } else {
      setError(response.error || 'Registration failed');
    }
    
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="whitelist-registration">
        <Card>
          <Card.Body className="text-center">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p>Loading your whitelist information...</p>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="registration-success">
        <Card className="text-center">
          <Card.Body>
            <div className="success-icon mb-3">
              <i className="fas fa-check-circle text-success" style={{ fontSize: '3rem' }}></i>
            </div>
            <h3 className="text-success mb-3">Registration Added to Cart!</h3>
            <p className="text-muted mb-4">
              <strong>{category.name}</strong> has been successfully added to your cart.
            </p>
            <p className="text-muted mb-4">
              You can now proceed to checkout to complete your purchase and finalize your registration.
            </p>
            <div className="d-flex gap-3 justify-content-center">
              <Button variant="outline-secondary" onClick={onCancel}>
                Continue Shopping
              </Button>
              <Button 
                variant="primary" 
                onClick={() => window.location.href = '/cart/'}
              >
                <i className="fas fa-shopping-cart me-2"></i>
                Go to Checkout
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (!whitelistData) {
    return (
      <div className="whitelist-registration">
        <Card>
          <Card.Body className="text-center">
            <Alert variant="danger">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error || 'Unable to load whitelist information'}
            </Alert>
            <Button variant="outline-secondary" onClick={onCancel}>
              Close
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="whitelist-registration">
      <Card>
        <Card.Header>
          <h4 className="mb-0">Welcome, {whitelistData.whitelist_request.name}!</h4>
          <p className="text-muted mb-0">Your whitelist request has been approved for {event.title}</p>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}

          {/* Your Information Section */}
          <div className="data-section mb-4">
            <h5 className="section-title">Your Information</h5>
            <div className="data-grid">
              <div className="data-item">
                <div className="data-label">Name</div>
                <div className="data-value">{whitelistData.whitelist_request.name}</div>
              </div>
              <div className="data-item">
                <div className="data-label">Email</div>
                <div className="data-value">{whitelistData.whitelist_request.email}</div>
              </div>
              <div className="data-item">
                <div className="data-label">Phone</div>
                <div className="data-value">{whitelistData.whitelist_request.phone || 'Not provided'}</div>
              </div>
            </div>
          </div>

          {/* Additional Data Section */}
          {whitelistData.whitelist_data && Object.keys(whitelistData.whitelist_data).length > 0 && (
            <div className="data-section mb-4">
              <h5 className="section-title">Additional Information</h5>
              <div className="data-grid">
                {Object.entries(whitelistData.whitelist_data).map(([key, value]) => (
                  <div key={key} className="data-item">
                    <div className="data-label">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                    <div className="data-value">
                      {typeof value === 'string' && (value.startsWith('data:') || value.startsWith('http') || value.startsWith('/')) ? (
                        <a href={value} target="_blank" rel="noopener noreferrer" className="file-link">
                          View File
                        </a>
                      ) : (
                        String(value)
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Category Display Section */}
          <div className="category-selection">
            <h5 className="section-title">Registration Category</h5>
            <div className="category-display mb-4">
              <div className="category-name">{category.name}</div>
              <div className="category-price">
                {category.price === 0 ? 'Free' : `${event.setting.currency?.symbol || '$'}${category.price}`}
              </div>
              {category.description && (
                <div className="category-description">{category.description}</div>
              )}
              <div className="category-remaining">
                {category.sold_out ? (
                  <span style={{ color: '#dc3545' }}>Sold Out</span>
                ) : (
                  event.setting.show_remaining && (
                    <span>{category.remaining} remaining</span>
                  )
                )}
              </div>
            </div>
            
            <Form onSubmit={handleSubmit}>
              <div className="registration-actions">
                <Button
                  type="button"
                  variant="link"
                  onClick={onCancel}
                  className="btn-cancel"
                >
                  <i className="fas fa-times"></i>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={submitting || category.sold_out}
                >
                  {submitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Processing...
                    </>
                  ) : (
                    'Continue to Purchase'
                  )}
                </Button>
              </div>
            </Form>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default WhitelistRegistration;
